import React, { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import { useGetAiUserPreferences, useModifyAiUserPreferences } from '../../../../../api/denaliAssistant'
import Loading from '../../../../../components/molecules/Loading'
import PageLayout from '../../common/PageLayout'
import PageHeading from '../../common/PageHeading'
import Divider from '../../../../../components/atoms/Divider'
import FlexRow from '../../../../../components/molecules/FlexRow'
import SydButton from '../../../../../components/commonDesign/Button'
import Alert from '../../../../../components/atoms/Alert'
import PreferenceField from './PreferenceField'

function PreferencesHome () {
  const { data, isLoading } = useGetAiUserPreferences()
  const { mutateAsync: modifyPreferences } = useModifyAiUserPreferences()
  const [processing, setProcessing] = useState(false)

  // Helper function to convert dot notation to form-safe keys
  const toFormKey = (code) => code.replace(/\./g, '__')

  // Transform profile data into form default values
  const defaultValues = useMemo(() => {
    if (!data?.profile) return {}

    const values = {}
    Object.entries(data.profile).forEach(([code, profileValue]) => {
      // Convert dots to double underscores for form keys
      values[toFormKey(code)] = profileValue.value
    })
    return values
  }, [data])

  const form = useForm({
    mode: 'onChange',
    defaultValues,
    values: defaultValues // Update form when data changes
  })

  const handleResetToDefault = useCallback((code) => {
    // Convert API code to form key and reset to default by setting value to null
    const formKey = toFormKey(code)
    form.setValue(formKey, null, { shouldDirty: true })
  }, [form])

  const handleSubmit = useCallback(async (formData) => {
    try {
      setProcessing(true)

      // Build changes object, converting form keys back to API keys
      const changes = {}
      data.schema.forEach((schemaItem) => {
        const apiCode = schemaItem.code
        const formKey = toFormKey(apiCode)

        if (form.formState.dirtyFields[formKey]) {
          // Get value from form and use the API code as the key
          const value = form.getValues(formKey)
          changes[apiCode] = value
        }
      })

      // If no changes, don't call the API
      if (Object.keys(changes).length === 0) {
        setProcessing(false)
        return
      }

      console.log('Sending changes:', JSON.stringify({ changes })) // Debug log to verify format

      await modifyPreferences(changes)

      // Reset form dirty state after successful save
      form.reset(formData)
    } catch (err) {
      console.error('Failed to save preferences:', err)
      form.setError('root', {
        type: 'custom',
        message: err.message || 'Failed to save preferences'
      })
    } finally {
      setProcessing(false)
    }
  }, [form, modifyPreferences, data])

  const handleCancel = useCallback(() => {
    form.reset()
  }, [form])

  const onSubmit = useCallback(async (e) => {
    // Clear previous errors before resubmitting
    form.clearErrors()

    const onValid = async (formData) => {
      await handleSubmit(formData)
    }
    const onInvalid = (errors) => {
      console.error('Form validation errors:', errors)
    }

    const handler = form.handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [form, handleSubmit])

  if (isLoading) {
    return (
      <Loading />
    )
  }

  if (!data?.schema || !data?.profile) {
    return (
      <PageLayout className='font-serif' variant='constrained'>
        <div>No preferences available</div>
      </PageLayout>
    )
  }

  return (
    <PageLayout className='font-serif' variant='constrained'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PageHeading title='AI Preferences' />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {data.schema.map((schemaItem) => (
          <PreferenceField
            key={schemaItem.code}
            schemaItem={schemaItem}
            formKey={toFormKey(schemaItem.code)}
            profileValue={data.profile[schemaItem.code]}
            control={form.control}
            onResetToDefault={handleResetToDefault}
          />
        ))}

        <Grid item xs={12}>
          <Divider />
        </Grid>

        {form.formState.errors.root && (
          <Grid item xs={12}>
            <Alert severity='error'>
              {form.formState.errors.root.message}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <FlexRow className='gap-md justify-end'>
            <SydButton
              size='lg'
              variant='ghost'
              onClick={handleCancel}
              disabled={processing}
            >
              Cancel
            </SydButton>
            <SydButton
              size='lg'
              variant='primary'
              onClick={onSubmit}
              processing={processing}
              disabled={!form.formState.isDirty}
            >
              Save Preferences
            </SydButton>
          </FlexRow>
        </Grid>
      </Grid>
    </PageLayout>
  )
}

export default PreferencesHome
